import { DRAWING_COLORS, type DrawingKind } from '@/lib/drawings'

/**
 * Barre des outils de dessin.
 *
 * Repliée par défaut : sur un téléphone, quatre outils et six couleurs
 * en permanence prendraient la place du graphique. Un appui les déplie.
 *
 * L'outil se désarme tout seul après un tracé. C'est délibéré : rester en mode
 * « ligne » fait poser un trait à chaque fois qu'on essaie de faire défiler le
 * graphique, et on ne comprend pas d'où ils viennent.
 */
export function DrawingTools({
  open,
  onToggle,
  tool,
  onChangeTool,
  color,
  onChangeColor,
  count,
  onClear,
}: {
  open: boolean
  onToggle: () => void
  tool: DrawingKind | null
  onChangeTool: (tool: DrawingKind | null) => void
  color: string
  onChangeColor: (color: string) => void
  /** Nombre de tracés sur cette paire, pour proposer l'effacement. */
  count: number
  onClear: () => void
}) {
  const outils: { key: DrawingKind; label: string; hint: string }[] = [
    { key: 'level', label: '─', hint: 'Support / résistance' },
    { key: 'segment', label: '╱', hint: 'Segment' },
    { key: 'ruler', label: '⇔', hint: 'Mesurer en pips' },
  ]

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={open}
        className={`min-h-touch rounded-md border px-3 text-[12px] transition-colors ${
          open || tool
            ? 'border-accent bg-accent/15 text-accent'
            : 'border-border bg-elevated text-ink-muted hover:bg-border/60'
        }`}
      >
        ✎ Tracés
        {count > 0 && <span className="num ml-1 text-[10px] opacity-70">{count}</span>}
      </button>

      {open && (
        <div className="flex w-full flex-wrap items-center gap-2 rounded-md border border-border bg-elevated/60 p-1.5">
          <div className="flex gap-1">
            {outils.map((outil) => {
              const actif = tool === outil.key
              return (
                <button
                  key={outil.key}
                  type="button"
                  title={outil.hint}
                  aria-label={outil.hint}
                  aria-pressed={actif}
                  onClick={() => onChangeTool(actif ? null : outil.key)}
                  className={`h-touch w-11 rounded text-[16px] font-bold transition-colors ${
                    actif
                      ? 'bg-accent text-white'
                      : 'bg-panel text-ink-muted hover:bg-border/60'
                  }`}
                >
                  {outil.label}
                </button>
              )
            })}
          </div>

          <div className="flex gap-1">
            {DRAWING_COLORS.map((teinte) => (
              <button
                key={teinte}
                type="button"
                aria-label={`Couleur ${teinte}`}
                onClick={() => onChangeColor(teinte)}
                style={{ backgroundColor: teinte }}
                className={`h-8 w-8 rounded-full transition-transform ${
                  color === teinte ? 'scale-110 ring-2 ring-white' : 'opacity-70'
                }`}
              />
            ))}
          </div>

          {count > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="min-h-touch rounded border border-down/50 px-2 text-[11px] text-down active:bg-down/10"
            >
              Tout effacer
            </button>
          )}

          <p className="basis-full text-[10px] leading-snug text-ink-muted">
            {tool === 'level'
              ? 'Touchez le graphique à la hauteur voulue.'
              : tool
                ? 'Deux points : un premier appui, puis un second.'
                : 'Choisissez un outil, puis touchez le graphique. Appuyez sur un tracé pour le supprimer.'}
          </p>
        </div>
      )}
    </>
  )
}
